import { FC, useMemo, useState } from 'react';

import { IoMdFingerPrint } from 'react-icons/io';

import { isAdminProfile, isAdminUser } from '~/common/utils/authorization';
import { useAuth } from '~/common/providers/auth';
import { ISidebarItem } from '~/common/interfaces/i-sidebar-item';
import { sidebar } from '~/common/constants/sidebar';
import { profilesIds } from '~/common/constants/profiles-ids';
import { permissionIds } from '~/common/constants/permission-ids';

import { SidebarSubmenu } from './Submenu';
import { SidebarMenuContainer } from './styles';
import { SidebarNav } from './Nav';

export const SidebarMenu: FC = () => {
  const { hasPermission, user } = useAuth();
  const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null);
  const auditLogsMenuKey = 'logs-de-auditoria';
  const settingsMenuKey = 'configuracoes';
  const sidebarOrder = [
    'estatisticas',
    'configuracoes',
    'st',
    'importacoes',
    auditLogsMenuKey,
    'perfis',
    'usuarios',
    'rh',
    'despesas',
    'veiculos',
    'comercial',
    'treinamentos',
  ];

  const orderSidebarItems = (items: ISidebarItem[]): ISidebarItem[] => {
    return [...items].sort((left, right) => {
      const leftIndex = sidebarOrder.indexOf(left.key);
      const rightIndex = sidebarOrder.indexOf(right.key);

      return (
        (leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex) -
        (rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex)
      );
    });
  };

  const isEmployeeWithCustomProfile = useMemo(() => {
    return user && user.profile && user.profile.id !== profilesIds.EMPLOYEE;
  }, [user]);

  const sidebarWithPermissions = useMemo(() => {
    if (!user || !user.profile) {
      return [];
    }

    const employeeSelfMenuItem: ISidebarItem | null =
      isEmployeeWithCustomProfile && user.employeeId
        ? {
            key: 'funcionarios/eu',
            label: 'Meus Dados',
            href: `/funcionarios/eu/${user.employeeId}`,
            icon: IoMdFingerPrint,
          }
        : null;

    if (isAdminProfile(user.profile)) {
      const canUseAdminOnlyMenus = isAdminUser(user);
      const canSeeSt = hasPermission([permissionIds.ST_VIEW]);
      const canSeeVehicles = hasPermission([permissionIds.VEHICLES_VIEW]);

      const adminOnlyKeys = new Set([
        auditLogsMenuKey,
        settingsMenuKey,
        'importacoes',
      ]);

      if (!canSeeSt) {
        adminOnlyKeys.add('st');
      }

      if (!canSeeVehicles) {
        adminOnlyKeys.add('veiculos');
      }

      const adminSidebar = canUseAdminOnlyMenus
        ? orderSidebarItems(sidebar)
        : orderSidebarItems(
            sidebar.filter((item) => !adminOnlyKeys.has(item.key))
          );

      if (!employeeSelfMenuItem) {
        return adminSidebar;
      }

      if (canUseAdminOnlyMenus) {
        return [
          adminSidebar[0],
          employeeSelfMenuItem,
          ...adminSidebar.slice(1),
        ];
      }

      return [employeeSelfMenuItem, ...adminSidebar];
    }

    const checkIfItIsEqualsLowerCaseNormalized = (
      left: string,
      right: string
    ): boolean => {
      return (
        left
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '') ===
        right
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
      );
    };

    const canSeeDashboard = hasPermission([
      permissionIds.DASHBOARD_FINANCIAL_VIEW,
      permissionIds.DASHBOARD_HUMAN_RESOURCES_VIEW,
      permissionIds.DASHBOARD_COMMERCIAL_VIEW,
      permissionIds.DASHBOARD_SERVICE_WORKS_VIEW,
      permissionIds.DASHBOARD_TST_VIEW,
    ]);

    const filtered: ISidebarItem[] = canSeeDashboard
      ? [
          sidebar.find((item) =>
            checkIfItIsEqualsLowerCaseNormalized(item.key, 'estatisticas')
          )!,
        ]
      : [];

    const canSeeUsers = hasPermission([permissionIds.USERS_LIST]);
    const canSeeProfiles = hasPermission([permissionIds.PROFILES_LIST]);

    if (canSeeProfiles) {
      filtered.push(
        sidebar.find((item) =>
          checkIfItIsEqualsLowerCaseNormalized(item.key, 'perfis')
        )!
      );
    }

    if (canSeeUsers) {
      filtered.push(
        sidebar.find((item) =>
          checkIfItIsEqualsLowerCaseNormalized(item.key, 'usuarios')
        )!
      );
    }

    const canSeeSt = hasPermission([permissionIds.ST_VIEW]);

    if (canSeeSt) {
      filtered.push(
        sidebar.find((item) =>
          checkIfItIsEqualsLowerCaseNormalized(item.key, 'st')
        )!
      );
    }

    const canSeeClients = hasPermission([permissionIds.CUSTOMERS_LIST]);

    const canSeeSuppliers = hasPermission([permissionIds.SUPPLIERS_LIST]);

    const canSeeEmployees = hasPermission([permissionIds.EMPLOYEES_VIEW]);
    const canSeeEmployeePayroll =
      hasPermission([permissionIds.PAYROLL_MANAGE]) &&
      hasPermission([permissionIds.EMPLOYEE_SALARY_VIEW]);
    const canSeeEmployeesClockin = hasPermission([permissionIds.CLOCKIN_VIEW]);
    const canSeeEmployeeDocuments = hasPermission([
      permissionIds.EMPLOYEE_DOCUMENTS_VIEW,
    ]);
    const canManageHolidays = hasPermission([permissionIds.HOLIDAYS_MANAGE]);
    const canSeeServiceWorkExpenses = hasPermission([
      permissionIds.SERVICE_WORK_EXPENSES_VIEW,
    ]);

    if (
      canSeeEmployees ||
      canSeeEmployeePayroll ||
      canSeeEmployeesClockin ||
      canSeeEmployeeDocuments ||
      canManageHolidays ||
      canSeeServiceWorkExpenses
    ) {
      const group = sidebar.find((item) =>
        checkIfItIsEqualsLowerCaseNormalized(item.key, 'rh')
      )!;

      filtered.push({
        ...group,
        children: group.children!.filter((child) => {
          if (checkIfItIsEqualsLowerCaseNormalized(child.key, 'funcionarios')) {
            return canSeeEmployees;
          }

          if (checkIfItIsEqualsLowerCaseNormalized(child.key, 'avaliacoes')) {
            return canSeeEmployees;
          }

          if (
            checkIfItIsEqualsLowerCaseNormalized(child.key, 'folha-salarial')
          ) {
            return canSeeEmployeePayroll;
          }

          if (checkIfItIsEqualsLowerCaseNormalized(child.key, 'feriados')) {
            return canManageHolidays;
          }

          if (
            checkIfItIsEqualsLowerCaseNormalized(child.key, 'cartao-de-ponto')
          ) {
            return canSeeEmployeesClockin;
          }

          if (checkIfItIsEqualsLowerCaseNormalized(child.key, 'atestados')) {
            return canSeeEmployeeDocuments;
          }

          if (
            checkIfItIsEqualsLowerCaseNormalized(child.key, 'despesas-obra')
          ) {
            return canSeeServiceWorkExpenses;
          }

          return false;
        }),
      });
    }

    const canSeeExpenses = hasPermission([permissionIds.EXPENSE_PRODUCTS_VIEW]);
    const canSeePurchaseOrders = hasPermission([
      permissionIds.PURCHASE_ORDERS_VIEW,
    ]);
    const canSeePurchaseInvoices = hasPermission([
      permissionIds.PURCHASE_INVOICES_VIEW,
    ]);

    if (
      canSeeExpenses ||
      canSeePurchaseOrders ||
      canSeeSuppliers ||
      canSeePurchaseInvoices
    ) {
      const group = sidebar.find((item) =>
        checkIfItIsEqualsLowerCaseNormalized(item.key, 'despesas')
      )!;

      const children = [];
      if (canSeeExpenses) {
        children.push(
          group.children!.find((child) =>
            checkIfItIsEqualsLowerCaseNormalized(child.key, 'cadastros')
          )!
        );
      }

      if (canSeePurchaseOrders) {
        children.push(
          group.children!.find((child) =>
            checkIfItIsEqualsLowerCaseNormalized(child.key, 'pedidos')
          )!
        );
      }

      if (canSeeSuppliers) {
        children.push(
          group.children!.find((child) =>
            checkIfItIsEqualsLowerCaseNormalized(child.key, 'fornecedores')
          )!
        );
      }

      if (canSeePurchaseInvoices) {
        children.push(
          group.children!.find((child) =>
            checkIfItIsEqualsLowerCaseNormalized(child.key, 'notas-fiscais')
          )!
        );
      }

      filtered.push({ ...group, children });
    }

    const canSeeVehicles = hasPermission([permissionIds.VEHICLES_VIEW]);

    if (canSeeVehicles) {
      const group = sidebar.find((item) =>
        checkIfItIsEqualsLowerCaseNormalized(item.key, 'veiculos')
      )!;

      filtered.push(group);
    }

    const canSeeServiceWorkds = hasPermission([
      permissionIds.SERVICE_WORKS_VIEW,
    ]);
    const canSeeAssets = hasPermission([
      permissionIds.ASSETS_VIEW,
      permissionIds.ASSET_LINKS_VIEW,
    ]);
    const canSeeTools = hasPermission([
      permissionIds.TOOLS_VIEW,
      permissionIds.TOOL_LINKS_VIEW,
    ]);

    // Grupo Comercial: Projetos e ARTs sob SERVICE_WORKS_VIEW (ARTs são
    // documentos de obra), Patrimônio sob ASSETS_VIEW, Ferramentas sob
    // TOOLS_VIEW e Clientes sob CUSTOMERS_LIST.
    if (canSeeServiceWorkds || canSeeAssets || canSeeTools || canSeeClients) {
      const group = sidebar.find((item) =>
        checkIfItIsEqualsLowerCaseNormalized(item.key, 'comercial')
      )!;

      const children = group.children!.filter((child) => {
        if (checkIfItIsEqualsLowerCaseNormalized(child.key, 'propostas')) {
          return canSeeServiceWorkds;
        }

        if (checkIfItIsEqualsLowerCaseNormalized(child.key, 'arts')) {
          return canSeeServiceWorkds;
        }

        if (checkIfItIsEqualsLowerCaseNormalized(child.key, 'aprs')) {
          return canSeeServiceWorkds;
        }

        if (checkIfItIsEqualsLowerCaseNormalized(child.key, 'clientes')) {
          return canSeeClients;
        }

        if (checkIfItIsEqualsLowerCaseNormalized(child.key, 'patrimonio')) {
          return canSeeAssets;
        }

        if (checkIfItIsEqualsLowerCaseNormalized(child.key, 'ferramentas')) {
          return canSeeTools;
        }

        return false;
      });

      if (children.length) {
        filtered.push({ ...group, children });
      }
    }

    if (hasPermission([permissionIds.TRAININGS_VIEW])) {
      filtered.push(
        sidebar.find((item) =>
          checkIfItIsEqualsLowerCaseNormalized(item.key, 'treinamentos')
        )!
      );
    }

    return employeeSelfMenuItem
      ? [employeeSelfMenuItem, ...filtered]
      : filtered;
  }, [
    auditLogsMenuKey,
    hasPermission,
    isEmployeeWithCustomProfile,
    settingsMenuKey,
    user,
  ]);

  return (
    <SidebarMenuContainer>
      {sidebarWithPermissions.map((menu, index) => {
        if (!menu) {
          return null;
        }

        return (menu.children || []).length > 0 ? (
          <SidebarSubmenu
            key={menu.key}
            submenu={menu}
            index={index}
            openSubmenuId={openSubmenuId}
            setOpenSubmenuId={setOpenSubmenuId}
          />
        ) : (
          <SidebarNav key={menu.key} nav={menu} index={index} />
        );
      })}
    </SidebarMenuContainer>
  );
};
